import { isEmpty, uniq } from 'lodash'

// =============================================================================
// Pure helpers for the Agent Studio language settings UI (AVR-678). The
// catalog (GET /prompts/languages/catalog) is server-owned and grows — nothing
// here hardcodes a language. `languages` order is the operator's order and is
// never sorted: the first entry is the default start language.
// =============================================================================

// A previewed LANGUAGE text is only valid for the set it was rendered for, so
// it is stored and read under this key: as the operator edits the selection,
// several previews can be in flight and an earlier one must not be mistaken
// for the current selection's text.
export const languagePreviewKey = (languages = [], startLanguage) =>
  `${languages.join(',')}|${startLanguage ?? ''}`

// Version `content` is the stored JSON string with snake_case keys
// ({"languages": [...], "start_language": "en", "stt_preset": "ink-2"}); null
// marks a reset row. `stt_preset` (the ASR model) only exists on
// single-language versions. Everything else reads the unpacked fields.
export const parseLanguageVersionContent = (content) => {
  if (!content) {
    return null
  }
  try {
    const parsed = JSON.parse(content)
    if (!parsed || !Array.isArray(parsed.languages)) {
      return null
    }
    return {
      languages: parsed.languages,
      startLanguage: parsed.start_language ?? parsed.languages[0] ?? null,
      // Left undefined when the key is absent, which is how a version says it
      // ran on the generated block rather than on wording of its own.
      instructions: parsed.instructions,
    }
  } catch (parseError) {
    return null
  }
}

// Two or more languages is the switch for everything the backend derives: it
// turns Cartesia's language detection on for the call, which routes speech
// recognition to Original; one language uses Ink-2. Derived from the count, not
// chosen — Cartesia's per-call API has no way to ask for Original on one
// language, so the ASR model stopped being an operator decision (2026-09-18).
export const isMultilingual = (languages) => (languages?.length ?? 0) > 1

// A stored code missing from the catalog can only happen if the catalog
// shrinks — render the code itself rather than hide the entry.
export const languageLabelFromCatalog = (catalog = [], code) =>
  catalog.find((entry) => entry.code === code)?.label || code

// One-line summary for history rows.
export const formatLanguageOverride = (parsed, catalog) => {
  if (!parsed) {
    return 'Reset to baseline'
  }
  const names = parsed.languages.map((code) => languageLabelFromCatalog(catalog, code)).join(', ')
  return `${names} · starts in ${languageLabelFromCatalog(catalog, parsed.startLanguage)}`
}

// Configured codes the voice cannot speak — the same rule the BE uses to 400 a
// save. `languages` is the full spoken set; `language` is only the native one
// (Skylar: language "en", languages ["en","hi","es","de"]). Until the BE ships
// `languages[]` there is nothing to gate on.
export const voiceMissingLanguages = (voice, languages = []) => {
  if (!Array.isArray(voice?.languages)) {
    return []
  }
  return languages.filter((code) => !voice.languages.includes(code))
}

// The language routes answer with `detail` as a string (shape validation, the
// anchor refusals, and the 502 when the voice-compatibility check's own
// provider lookup fails) or an object ({message, misses[]} for a voice gap);
// fetchingAPI rejects 400/502 with the raw body. Normalise to a `kind`
// discriminator, a message and display lines.
//
// The old `{message, agents[]}` provider shape is gone (2026-09-18): a save no
// longer writes to provider agents, so there is no per-agent fan-out to report.
// A 502 still exists and still means nothing was stored — it now arrives as a
// plain string and lands in `validation`, which renders it verbatim.
export const languageErrorDetail = (error, fallback, catalog) => {
  const detail = error?.detail
  if (typeof detail === 'string') {
    return { kind: 'validation', message: detail, items: [] }
  }
  if (detail && typeof detail === 'object') {
    const message = detail.message || fallback
    if (Array.isArray(detail.misses)) {
      return {
        kind: 'voice',
        message,
        // Two linked agents sharing one voice produce byte-identical lines, and
        // the panel keys the list by the rendered string — dedupe here.
        items: uniq(
          detail.misses.map(
            (miss) =>
              `${miss.voiceName || miss.voiceId} doesn't support ${languageLabelFromCatalog(
                catalog,
                miss.language
              )}`
          )
        ),
      }
    }
    return { kind: 'unknown', message, items: [] }
  }
  if (typeof error === 'string' && !isEmpty(error)) {
    return { kind: 'unknown', message: error, items: [] }
  }
  return { kind: 'unknown', message: error?.message || fallback, items: [] }
}
